import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { resolveColor } from '../../theme/colorTokens.js';
import { ALIGN_OPTIONS, marginFields } from '../_shared/blockFields.js';
import { marginStyle, num, str } from '../_shared/blockRender.js';
import { useCheckoutState, useProductState } from '../_shared/productContext.js';
import { ALIGN_ITEMS, orderCtaStyle } from '../_shared/orderAction.js';
/**
 * Shrine `buy-buttons` — config-by-config. Reproduces the CORE authoring settings of Shopify Shrine
 * Pro's Buy Buttons block, grouped exactly as Shrine groups them:
 *  - "Buttons & icons": uppercase_text, icon_scale, icon_spacing.
 *  - "Layout": width (% of the column, 0–100) + alignment + mobile alignment — ours, so the buttons can be
 *    made narrower than full width and positioned left/center/right (e.g. a centred 60%-wide ATC inside a
 *    featured-product column). Width 100% (the default) = the classic full-width button; alignment then has
 *    no visible effect. Mirrors the standalone Image block's Width/Alignment controls for consistency.
 *  - "Main ATC button": atc_custom_label (Line 1), atc_label_size, atc_label_2 (Line 2) + size + font,
 *    display_price (+ price_position + price_separator), enable_custom_color (+ custom_color + text).
 *  - "Secondary Skip cart ATC button": enable_secondary_btn (+ label + custom color).
 *  - "Margins" (24/24).
 *
 * DEFERRED (Shrine settings intentionally NOT reproduced here):
 *  - Shrine "Quantity selector" group (`atc_append` position + `atc_append_heights`) — governs an INLINE
 *    quantity stepper rendered beside the ATC button. Ours ships the quantity stepper as its own separate
 *    "Quantity selector" block, and buy-buttons never renders one inline, so those settings had no effect
 *    and were dropped (re-add here only if/when buy-buttons renders an inline stepper).
 *  - `show_dynamic_checkout` + `skip_cart` (General) — backend/checkout behaviour, not visual; wired with
 *    the real cart/checkout flow.
 *  - `prefix_icon` / `suffix_icon` (+ icon_1_attach / icon_2_attach) and the secondary button's
 *    `secondary_btn_prefix_icon` / `secondary_btn_suffix_icon` — image_picker icon uploads; deferred
 *    until the block schema gains an image-picker-backed icon control + asset wiring. `icon_scale` /
 *    `icon_spacing` are kept (they govern icon sizing once icons land) but currently have no rendered
 *    icon to size, and the `icon_*_attach` line-targeting selects only make sense once icons render.
 *
 * Line-2 tuning knobs Shrine carries (atc_label_2_uppercase → `atcLabel2Uppercase`, atc_lines_spacing
 * → `atcLinesSpacing`, atc_button_padding_y → `atcButtonPaddingY`) are now exposed under "Main ATC
 * button" (visible only when Line 2 is set, matching Shrine's visible_if).
 */
export const buyButtonsBlockSchema = {
    type: 'buy-buttons',
    name: 'Buy buttons',
    max: 1,
    // L'autre façon d'encaisser (carte) : même rôle d'ancre pour le composeur IA.
    checkout: true,
    fields: [
        // ── Layout ───────────────────────────────────────────────────────────────
        {
            id: 'width',
            type: 'range',
            label: 'Width',
            group: 'Layout',
            info: 'Button width as a % of the column. 100% = full width.',
            min: 20,
            max: 100,
            step: 5,
            default: 100,
            unit: '%',
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            group: 'Layout',
            info: 'Positions the buttons when narrower than full width.',
            default: 'center',
            options: ALIGN_OPTIONS,
        },
        {
            id: 'mobileAlignment',
            type: 'select',
            label: 'Mobile alignment',
            group: 'Layout',
            default: 'center',
            options: ALIGN_OPTIONS,
        },
        // ── Buttons & icons ──────────────────────────────────────────────────────
        {
            id: 'uppercaseText',
            type: 'checkbox',
            label: 'Uppercase button labels',
            group: 'Buttons & icons',
            default: true,
        },
        {
            id: 'iconScale',
            type: 'range',
            label: 'Icons scale',
            group: 'Buttons & icons',
            info: 'Related to button label font size',
            min: 80,
            max: 180,
            step: 5,
            default: 120,
            unit: '%',
        },
        {
            id: 'iconSpacing',
            type: 'range',
            label: 'Icons spacing',
            group: 'Buttons & icons',
            info: 'Empty space between the button label & the icons',
            min: 0,
            max: 40,
            step: 2,
            default: 10,
            unit: 'px',
        },
        // ── Main ATC button ──────────────────────────────────────────────────────
        {
            id: 'atcCustomLabel',
            type: 'text',
            label: 'Label - Line 1',
            group: 'Main ATC button',
            info: 'If empty, the default Add to Cart label will be displayed.',
            maxLength: 60,
        },
        {
            id: 'atcLabelSize',
            type: 'range',
            label: 'Line 1 font size',
            group: 'Main ATC button',
            min: 10,
            max: 30,
            step: 1,
            default: 18,
            unit: 'px',
        },
        {
            id: 'atcLabel2',
            type: 'text',
            label: 'Label - Line 2',
            group: 'Main ATC button',
            info: 'Optional secondary line of text displayed under Line 1. Leave empty to disable.',
            maxLength: 60,
        },
        {
            id: 'atcLabel2Size',
            type: 'range',
            label: 'Line 2 font size',
            group: 'Main ATC button',
            min: 8,
            max: 24,
            step: 1,
            default: 12,
            unit: 'px',
            visibleWhen: { field: 'atcLabel2', notEquals: '' },
        },
        {
            id: 'atcLabel2Font',
            type: 'select',
            label: 'Line 2 font',
            group: 'Main ATC button',
            default: 'body',
            options: [
                { value: 'body', label: 'Body' },
                { value: 'body-bold', label: 'Body bold' },
                { value: 'headings', label: 'Headings' },
            ],
            visibleWhen: { field: 'atcLabel2', notEquals: '' },
        },
        {
            id: 'atcLabel2Uppercase',
            type: 'checkbox',
            label: 'Uppercase Line 2',
            group: 'Main ATC button',
            default: false,
            visibleWhen: { field: 'atcLabel2', notEquals: '' },
        },
        {
            id: 'atcLinesSpacing',
            type: 'range',
            label: 'Space between lines',
            group: 'Main ATC button',
            min: 0,
            max: 20,
            step: 1,
            default: 3,
            unit: 'px',
            visibleWhen: { field: 'atcLabel2', notEquals: '' },
        },
        {
            id: 'atcButtonPaddingY',
            type: 'range',
            label: 'Button vertical padding',
            group: 'Main ATC button',
            min: 0,
            max: 30,
            step: 1,
            default: 8,
            unit: 'px',
            visibleWhen: { field: 'atcLabel2', notEquals: '' },
        },
        {
            id: 'displayPrice',
            type: 'checkbox',
            label: 'Display price inside the button',
            group: 'Main ATC button',
            default: false,
        },
        {
            id: 'pricePosition',
            type: 'select',
            label: 'Price position',
            group: 'Main ATC button',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
            ],
            visibleWhen: { field: 'displayPrice', equals: true },
        },
        {
            id: 'priceSeparator',
            aiWritable: false, // 0059 — machine format, not prose
            type: 'text',
            label: 'Price separator',
            group: 'Main ATC button',
            default: '•',
            maxLength: 8,
            visibleWhen: { field: 'displayPrice', equals: true },
        },
        {
            id: 'enableCustomColor',
            type: 'checkbox',
            label: 'Enable custom button colors',
            group: 'Main ATC button',
            default: false,
        },
        {
            id: 'customColor',
            type: 'color',
            label: 'Button custom color',
            group: 'Main ATC button',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'enableCustomColor', equals: true },
        },
        {
            id: 'customTextColor',
            type: 'color',
            label: 'Button custom text color',
            group: 'Main ATC button',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'enableCustomColor', equals: true },
        },
        // ── Secondary Skip cart ATC button ───────────────────────────────────────
        {
            id: 'enableSecondaryBtn',
            type: 'checkbox',
            label: 'Display secondary Skip cart ATC button',
            group: 'Secondary Skip cart ATC button',
            default: false,
        },
        {
            id: 'secondaryBtnLabel',
            type: 'text',
            label: 'Button label',
            group: 'Secondary Skip cart ATC button',
            default: 'Acheter maintenant',
            defaultKey: 'product.buy_now',
            maxLength: 60,
            visibleWhen: { field: 'enableSecondaryBtn', equals: true },
        },
        {
            id: 'secondaryBtnEnableCustomColor',
            type: 'checkbox',
            label: 'Enable custom button color',
            group: 'Secondary Skip cart ATC button',
            default: false,
            visibleWhen: { field: 'enableSecondaryBtn', equals: true },
        },
        {
            id: 'secondaryBtnCustomColor',
            type: 'color',
            label: 'Button custom color',
            group: 'Secondary Skip cart ATC button',
            info: 'Applied when Enable custom button color is checked.',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'secondaryBtnEnableCustomColor', equals: true },
        },
        // ── Margins ──────────────────────────────────────────────────────────────
        ...marginFields(24, 24),
    ],
};
/** Map Shrine `atc_label_2_font` to the matching theme font-family / weight. */
function line2FontStyle(font) {
    switch (font) {
        case 'body-bold':
            return { fontFamily: 'inherit', fontWeight: 'var(--aqx-theme-fw-bold, 700)' };
        case 'headings':
            return {
                fontFamily: 'var(--aqx-theme-heading-font, var(--aqx-theme-font))',
                fontWeight: 'var(--aqx-theme-fw-bold, 700)',
            };
        case 'body':
        default:
            return { fontFamily: 'inherit', fontWeight: 'var(--aqx-theme-fw-regular, 400)' };
    }
}
/**
 * Bold squared "Add to cart" (Shrine `.button`, 6px radius var) + optional outline secondary
 * "Buy It Now" (Shrine `.button--secondary`).
 *
 * feature 0026 (single-method buy-box) — inside a `main-product` buy-box ({@link useCheckoutState} is
 * present) this block is SUPERSEDED by the sibling `cod-form` (the one unified buy-box) and renders NOTHING;
 * see the `if (cs) return null;` guard in {@link BuyButtonsBlock}. It renders its buttons only OUTSIDE a
 * checkout provider (e.g. `featured-product`), where it keeps its standalone demo add-to-cart behaviour.
 */
const BuyButtonsBlock = ({ block, context }) => {
    const ps = useProductState();
    const cs = useCheckoutState();
    // Feature 0026 (single-method buy-box): inside a `main-product` buy-box the unified "Boîte d'achat" (the
    // cod-form block) IS the one buy-box — buy-buttons is superseded there and renders NOTHING (this kills the
    // old stacked double buy-box, including on already-seeded stores). Outside a checkout provider (e.g.
    // `featured-product`) `cs` is null and it keeps its standalone add-to-cart behaviour. Hooks run first so
    // hook order stays stable across the early return.
    if (cs)
        return null;
    if (!ps)
        return null;
    return renderBuyButtons(block.settings, ps, context.formatMoney(ps.product.price), `aqx-bb-${block.id}`, context);
};
function renderBuyButtons(bs, ps, priceText, cls, context) {
    const { message, setMessage } = ps;
    const uppercase = bs.uppercaseText !== false;
    // ── Layout: width (% of the column) + desktop/mobile alignment. Legacy `buttonWidth: 'fit-content'`
    // (the old binary control) is still honored so older docs render unchanged; new docs use `width`. ──
    const legacyFit = str(bs.buttonWidth, '') === 'fit-content';
    const width = Math.min(num(bs.width, 100), 100);
    const btnInlineSize = legacyFit ? 'fit-content' : `${width}%`;
    const deskAlign = ALIGN_ITEMS[str(bs.alignment, 'center')] ?? 'center';
    const mobAlign = ALIGN_ITEMS[str(bs.mobileAlignment, 'center')] ?? 'center';
    // Horizontal alignment of the (possibly narrower-than-column) buttons lives in a scoped class so it can be
    // responsive: desktop base + mobile override at 750px, mirrored in @media (live) + @container (builder).
    const layoutCss = `.${cls}{align-items:${deskAlign}}` +
        `@media(max-width:749px){.${cls}{align-items:${mobAlign}}}` +
        `@container aqx-page (max-width:749px){.${cls}{align-items:${mobAlign}}}`;
    // ── Primary ATC colours (Shrine enable_custom_color → custom_color / custom_text_color). ──
    const customOn = bs.enableCustomColor === true;
    const atcBg = customOn
        ? (resolveColor(bs.customColor) ?? 'var(--aqx-scheme-accent)')
        : 'var(--aqx-scheme-accent)';
    const atcText = customOn
        ? (resolveColor(bs.customTextColor) ?? 'var(--aqx-theme-accent-contrast, #fff)')
        : 'var(--aqx-theme-accent-contrast, #fff)';
    // ── Labels (atc_custom_label fallback to the localized default; Line 2 optional). ──
    const line1 = str(bs.atcCustomLabel) || 'Ajouter au panier';
    const line2 = str(bs.atcLabel2);
    const line1Size = num(bs.atcLabelSize, 18);
    const line2Size = num(bs.atcLabel2Size, 12);
    const line2Font = line2FontStyle(str(bs.atcLabel2Font, 'body'));
    const line2Uppercase = bs.atcLabel2Uppercase === true;
    const linesSpacing = num(bs.atcLinesSpacing, 3);
    const buttonPaddingY = num(bs.atcButtonPaddingY, 8);
    // ── In-button price (display_price + price_position + price_separator). ──
    const displayPrice = bs.displayPrice === true;
    const priceLeft = str(bs.pricePosition, 'left') === 'left';
    const separator = str(bs.priceSeparator, '•');
    // Shrine `.button` base (shared with cod-form via orderCtaStyle) + this block's width-% + vertical-padding
    // overrides. Spreading then overriding keeps the emitted CSS byte-identical to the hand-written object.
    const cta = {
        ...orderCtaStyle(atcBg, atcText, uppercase),
        inlineSize: btnInlineSize,
        paddingBlock: `${buttonPaddingY}px`,
    };
    // Shrine `.button--secondary`: transparent fill, foreground text + 1px foreground outline.
    const secCustomOn = bs.secondaryBtnEnableCustomColor === true;
    const secColor = secCustomOn
        ? (resolveColor(bs.secondaryBtnCustomColor) ?? 'var(--aqx-scheme-text)')
        : 'var(--aqx-scheme-text)';
    const buyNow = {
        ...cta,
        background: 'transparent',
        color: secColor,
        border: `1px solid ${secColor}`,
        marginTop: '10px',
    };
    const secLabel = str(bs.secondaryBtnLabel) || t(context, 'product.buy_now');
    // The price chip injected inside the ATC button (separator between label and price).
    const priceChip = displayPrice ? (_jsxs("span", { style: { display: 'inline-flex', alignItems: 'center', gap: '0.5em', fontWeight: 700 }, children: [_jsx("span", { "aria-hidden": "true", style: { opacity: 0.7 }, children: separator }), _jsx("span", { children: priceText })] })) : null;
    // The stacked label (Line 1 + optional Line 2).
    const labelStack = (_jsxs("span", { style: {
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: `${linesSpacing}px`,
        }, children: [_jsx("span", { style: { fontSize: `${line1Size}px`, lineHeight: 1.1 }, children: line1 }), line2 ? (_jsx("span", { style: {
                    fontSize: `${line2Size}px`,
                    lineHeight: 1.1,
                    textTransform: line2Uppercase ? 'uppercase' : 'none',
                    letterSpacing: line2Uppercase ? '0.05em' : 'normal',
                    ...line2Font,
                }, children: line2 })) : null] }));
    // ── This block only renders OUTSIDE a `main-product` buy-box (the `if (cs) return null;` guard returns
    //    before we reach here), so it never places a real order. It used to flash "Ajouté au panier (démo)."
    //    under the button — a placeholder that reached real storefronts, so the CTA now stays silent. ──
    return (_jsxs("div", { className: cls, "data-aqx-buybox": "", style: { display: 'flex', flexDirection: 'column', ...marginStyle(bs, 24, 24) }, children: [_jsx("style", { dangerouslySetInnerHTML: { __html: layoutCss } }), _jsxs("button", { type: "button", style: cta, children: [displayPrice && priceLeft ? priceChip : null, labelStack, displayPrice && !priceLeft ? priceChip : null] }), bs.enableSecondaryBtn === true ? (_jsx("button", { type: "button", style: buyNow, onClick: () => setMessage(t(context, 'builder.redirect_payment_demo')), children: secLabel })) : null, message ? (_jsx("p", { role: "status", style: {
                    marginTop: 'var(--aqx-theme-space-3, 12px)',
                    fontSize: 'var(--aqx-theme-text-sm, 14px)',
                    color: 'var(--aqx-scheme-accent)',
                }, children: message })) : null] }));
}
export const buyButtonsBlock = {
    type: 'buy-buttons',
    schema: buyButtonsBlockSchema,
    Component: BuyButtonsBlock,
    meta: { label: 'Buy buttons' },
};
//# sourceMappingURL=index.js.map